import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import toast from 'react-hot-toast';

export function Login() {
  const { login } = useAuth();
  const { t } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Welcome back, ${data.user.name.split(' ')[0]}!`);
      navigate(data.user.role === 'admin' ? '/admin' : '/');
    } catch (err) { toast.error(err.response?.data?.message || 'Invalid credentials'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg-primary)' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <div style={{ background: 'linear-gradient(135deg, var(--accent), var(--gold))', borderRadius: 14, padding: 12 }}><Zap size={24} color="white" fill="white" /></div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700 }}>Tech<span style={{ color: 'var(--accent)' }}>Store</span></span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, marginBottom: 8 }}>{t('auth','login_title')}</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{t('auth','login_sub')}</p>
        </div>
        <div className="card" style={{ padding: 36 }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{t('auth','email')}</label>
              <input className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">{t('auth','password')}</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required style={{ paddingInlineEnd: 48 }} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? t('common','loading') : t('auth','sign_in')}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-secondary)', fontSize: 14 }}>
            {t('auth','no_account')} <Link to="/register" style={{ color: 'var(--accent)', fontWeight: 700 }}>{t('auth','sign_up')}</Link>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const { t } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success('Account created!');
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.message || 'Registration failed'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <div style={{ background: 'linear-gradient(135deg, var(--accent), var(--gold))', borderRadius: 14, padding: 12 }}><Zap size={24} color="white" fill="white" /></div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700 }}>Tech<span style={{ color: 'var(--accent)' }}>Store</span></span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, marginBottom: 8 }}>{t('auth','register_title')}</h1>
          <p style={{ color: 'var(--text-secondary)' }}>{t('auth','register_sub')}</p>
        </div>
        <div className="card" style={{ padding: 36 }}>
          <form onSubmit={handleSubmit}>
            {[['name', t('auth','name'), 'text', 'John Doe'], ['email', t('auth','email'), 'email', 'you@example.com'], ['password', t('auth','password'), 'password', '••••••••']].map(([key, label, type, ph]) => (
              <div key={key} className="form-group">
                <label className="form-label">{label}</label>
                <input className="form-input" type={type} placeholder={ph} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} required minLength={key === 'password' ? 6 : undefined} />
              </div>
            ))}
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? t('common','loading') : t('auth','sign_up')}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-secondary)', fontSize: 14 }}>
            {t('auth','have_account')} <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 700 }}>{t('auth','sign_in')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
